/**
 * Die Schriften, die ein **Canvas-Element** tragen kann — gewählt im
 * Schriftmenü der **Options-Leiste** (Figma `5083:51851`).
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`.
 */

/**
 * Der Font-Stack der App.
 *
 * **Ohne `var(--font-sans)`, obwohl es die Variable gibt:** Derselbe Stack geht
 * auch an `CanvasRenderingContext2D.font`, um die Schriftgröße eines
 * **Notiz** zu messen — und dort ist eine CSS-Variable nicht auflösbar; die
 * Zuweisung würde stillschweigend verworfen und jeder Zettel nach der falschen
 * Schrift bemessen. `system-ui` trifft auf beiden Plattformen dasselbe wie die
 * ersten Einträge des Tokens.
 */
const APP_STACK = "'Geist', system-ui, sans-serif"

export interface CanvasNoteFont {
  /**
   * Was in der Datenbank steht. `null` ist **Standard** — und Standard heißt
   * für BEIDE Arten dieselbe Schrift; ein Schlüssel, der je nach `kind` etwas
   * anderes bedeutete, wäre in einer gemischten Auswahl nicht zu erklären
   * (CONTEXT.md, „Schrift eines Canvas-Elements").
   */
  key: string | null
  /**
   * Der Name im Menü — der Name der Schrift, unübersetzt. Auch die Vorgabe
   * heißt nach ihrer Schrift („Geist"), nicht „Standard" (entschieden
   * 2026-09-23): Jeder andere Eintrag sagt, was man bekommt, und hinter der
   * Vorgabe steht immer dieselbe Schrift, in jedem Theme.
   */
  label: string
  /** Was als `font-family` gesetzt wird. */
  stack: string
  /**
   * Der Schnitt, in dem die Familie geladen ist (siehe die `@font-face`-Blöcke
   * in `globals.scss` — je Familie genau einer).
   *
   * **Alle acht stehen in Regular (400).** Ein gemeinsamer Schnitt ist die
   * einzige Grundlage, auf der acht Schriften vergleichbar wirken: Ein Wechsel
   * soll die Gestalt ändern, nicht das Gewicht.
   *
   * Ausdrücklich mitgeführt und nicht der CSS-Zuordnung überlassen: Diese
   * wählt bei fehlendem 500er zwar den vorhandenen Schnitt, aber erst nach
   * einer Regel, die man beim Lesen des SCSS nicht sieht. Steht die Zahl hier,
   * steht sie an einer Stelle.
   */
  weight: number
  /**
   * Das Gewicht des **Titels** einer Notiz — die eine Ausnahme von „alle acht
   * in Regular" (CONTEXT.md, Schrift eines Canvas-Elements): Der Titel soll
   * sich vom Text abheben, Medium war dafür zu wenig, Semibold zu viel.
   *
   * **550, wo die Familie variabel geladen ist** (Geist, JetBrains Mono,
   * Space Grotesk, Lora, Caveat — `font-weight: 400 700` o. ä. in
   * `globals.scss`): ein echter Zwischenwert. **600, wo es nur den
   * Regular-Schnitt gibt** (Kalam, Patrick Hand, Permanent Marker — variable
   * Fassungen existieren nicht): Dort zeichnet der Browser ab 600 einen
   * Fettdruck nach, und unter 600 gar nichts — 550 sähe dort aus wie Regular.
   * **Caveat 650**: Die Handschrift ist so dünn, dass 550 kaum vom Text
   * abstach.
   */
  titleWeight: number
  /**
   * Das Gewicht des **Projekttitels** in dieser Schrift, wo es von
   * `titleWeight` abweicht. Neben dem Titel steht dort **B** (700) — und das
   * muss sichtbar schwerer sein (gemeldet 2026-09-26): Bei den statischen
   * Schriften landeten 600 und 700 im selben Schnitt (Kalam Bold bzw. der
   * nachgezeichnete Fettdruck), bei Caveat lagen 650 und 700 zu dicht, und
   * JetBrains Mono wird als Festbreitenschrift kaum fetter — 550 zu 700 war
   * nicht zu sehen.
   */
  projectTitleWeight?: number
  /**
   * Wie weit der **Marker** in dieser Schrift verschoben wird, damit er so
   * mittig zur Schrift sitzt wie bei Geist (em, positiv = tiefer). Der Marker
   * richtet sich nach der Textbox, und die hängt an Ober- und Unterlänge der
   * Schrift: Kalam bringt 0,53 em Unterlänge mit (die anderen um 0,3 em), dort
   * saß der Marker sichtbar zu tief (gemeldet 2026-09-26).
   *
   * **Gemessen aus den `woff2`**, wie `scale`: Mitte der Textbox gegen die
   * Mitte zwischen halber x-Höhe und halber Versalhöhe, auf Geist bezogen.
   */
  markerShift: number
  /**
   * Wie weit die **Mitte der Kleinbuchstaben** unter der Mitte der Textbox
   * liegt (em) — dort geht das Durchstreichen im Projekttitel durch. Die
   * Linie des Browsers folgt dem Wert der Schrift und lag nicht mittig
   * (gemeldet 2026-09-26). Gemessen aus den `woff2`: Boxmitte minus halbe x-Höhe.
   */
  strikeShift: number
  /** Wie tief die Schrift ihre Unterstreichung legt (em unter der Grundlinie, `post.underlinePosition`). */
  underlineDepth: number
  /**
   * Der **optische Ausgleich**: Womit die Schriftgröße multipliziert wird,
   * damit diese Schrift so groß WIRKT wie die Standardschrift.
   *
   * Zwei Schriften in derselben `font-size` sind nicht gleich groß zu sehen —
   * entscheidend ist die x-Höhe, und die geht weit auseinander: Caveat bringt
   * 0,373 em, Permanent Marker 0,623. In 15px gesetzt wirkt Caveat dadurch
   * fast halb so groß.
   *
   * **Die Zahlen sind aus den `woff2` GEMESSEN**, nicht geschätzt (x-Höhe und
   * Versalhöhe über `fontTools`, gewichtet 80 : 20 und auf Geist normiert).
   * Die x-Höhe wiegt schwerer, weil Fließtext überwiegend aus Kleinbuchstaben
   * besteht; die Versalhöhe hält gegen, damit Schriften mit sehr flachen
   * Kleinbuchstaben nicht mit riesigen Großbuchstaben herauskommen.
   *
   * Nachzumessen: `x/em` und `H/em` je Datei, Faktor = `0.8 · (x_Geist/x) +
   * 0.2 · (H_Geist/H)`.
   */
  scale: number
}

/**
 * **Die Reihenfolge ist die des Menüs** (Figma `5083:51851`): Standard zuerst,
 * dann die sieben nach ihrem Charakter — Monospace, Grotesk, Serife, und
 * zuletzt die vier Handschriften von der ruhigsten zur lautesten.
 */
export const CANVAS_NOTE_FONTS: readonly CanvasNoteFont[] = [
  { key: null, label: 'Geist', stack: APP_STACK, weight: 400, titleWeight: 550, markerShift: 0, strikeShift: 0.09, underlineDepth: 0.1, scale: 1 },
  { key: 'jetbrains-mono', label: 'JetBrains Mono', stack: "'JetBrains Mono', ui-monospace, monospace", weight: 400, titleWeight: 550, projectTitleWeight: 500, markerShift: -0.005, strikeShift: 0.085, underlineDepth: 0.155, scale: 0.97 },
  { key: 'space-grotesk', label: 'Space Grotesk', stack: "'Space Grotesk', " + APP_STACK, weight: 400, titleWeight: 550, markerShift: 0.005, strikeShift: 0.105, underlineDepth: 0.1, scale: 1.07 },
  { key: 'lora', label: 'Lora', stack: "'Lora', Georgia, serif", weight: 400, titleWeight: 550, markerShift: 0.02, strikeShift: 0.115, underlineDepth: 0.1, scale: 1.05 },
  { key: 'caveat', label: 'Caveat', stack: "'Caveat', cursive", weight: 400, titleWeight: 650, projectTitleWeight: 550, markerShift: 0.02, strikeShift: 0.15, underlineDepth: 0.075, scale: 1.39 },
  { key: 'patrick-hand', label: 'Patrick Hand', stack: "'Patrick Hand', cursive", weight: 400, titleWeight: 600, projectTitleWeight: 400, markerShift: 0.04, strikeShift: 0.13, underlineDepth: 0.075, scale: 1.12 },
  { key: 'kalam', label: 'Kalam', stack: "'Kalam', cursive", weight: 400, titleWeight: 600, projectTitleWeight: 400, markerShift: -0.085, strikeShift: 0.01, underlineDepth: 0.125, scale: 1.06 },
  { key: 'permanent-marker', label: 'Permanent Marker', stack: "'Permanent Marker', cursive", weight: 400, titleWeight: 600, projectTitleWeight: 400, markerShift: 0.02, strikeShift: 0.1, underlineDepth: 0.075, scale: 0.87 },
] as const

/** Standard — für beide Arten dieselbe Schrift. */
export const DEFAULT_CANVAS_NOTE_FONT = CANVAS_NOTE_FONTS[0]!

/**
 * Der Eintrag zu einem Schlüssel. Unbekannte Schlüssel fallen auf **Standard**
 * zurück: Eine Zeile aus einer neueren Fassung, die eine Schrift nennt, die
 * dieses Gerät nicht kennt, soll lesbar bleiben.
 */
export function getCanvasNoteFont(key: string | null | undefined): CanvasNoteFont {
  return CANVAS_NOTE_FONTS.find(f => f.key === (key ?? null)) ?? DEFAULT_CANVAS_NOTE_FONT
}

/**
 * Die Schrift als Zeichenkette für `CanvasRenderingContext2D.font` — die Form,
 * die `computeStickyFontSize` zum Messen braucht.
 *
 * **Ohne optischen Ausgleich:** Gemessen wird in der Größe, in der auch
 * gezeichnet wird. Der Ausgleich sitzt eine Ebene höher, dort wo die Größe
 * bestimmt wird — hier eingerechnet, wäre er doppelt drin.
 *
 * @example
 * canvasFontFace('caveat', 100)   // "700 100px 'Caveat', cursive"
 */
export function canvasFontFace(key: string | null | undefined, sizePx: number): string {
  const font = getCanvasNoteFont(key)
  return `${font.weight} ${sizePx}px ${font.stack}`
}
